import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Breadcrumb = ({ items = [], showSchema = true }) => {
  const location = useLocation();
  
  // Generate breadcrumb items from current path if not provided
  const breadcrumbItems = items.length > 0 ? items : generateFromPath(location.pathname);
  
  // JSON-LD Schema for breadcrumbs
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": item.href ? `${window.location.origin}${item.href}` : undefined
    }))
  };

  if (breadcrumbItems.length === 0) return null;

  return (
    <>
      {showSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>
      )}
      
      <nav aria-label="Breadcrumb" className="py-3 border-b border-zinc-800">
        <div className="container mx-auto px-4">
          <ol className="flex items-center gap-2 text-sm flex-wrap">
            <li className="flex items-center">
              <Link 
                to="/" 
                className="text-zinc-500 hover:text-emerald-400 transition-colors flex items-center gap-1"
              >
                <Home className="w-4 h-4" />
                <span className="sr-only sm:not-sr-only">Home</span>
              </Link>
            </li>
            
            {breadcrumbItems.map((item, index) => (
              <li key={index} className="flex items-center">
                <ChevronRight className="w-4 h-4 text-zinc-600 mx-1" />
                {item.href && index < breadcrumbItems.length - 1 ? (
                  <Link 
                    to={item.href}
                    className="text-zinc-500 hover:text-emerald-400 transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-zinc-300 font-medium truncate max-w-[200px]">
                    {item.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
};

// Helper function to generate breadcrumbs from path
function generateFromPath(pathname) {
  const pathMap = {
    '/search': { label: 'Search', href: '/search' },
    '/freelancers': { label: 'Freelancers', href: '/freelancers' },
    '/contact': { label: 'Contact', href: '/contact' },
    '/blog': { label: 'Blog', href: '/blog' },
    '/favorites': { label: 'Favorites', href: '/favorites' },
    '/my-listings': { label: 'My Listings', href: '/my-listings' },
    '/create-listing': { label: 'Create Listing', href: '/create-listing' },
    '/admin': { label: 'Admin Panel', href: '/admin' },
    '/admin/settings': { label: 'Site Settings', href: '/admin/settings' },
    '/admin/blog': { label: 'Blog Management', href: '/admin/blog' },
    '/admin/seo': { label: 'SEO Management', href: '/admin/seo' },
  };

  const segments = pathname.split('/').filter(Boolean);
  const items = [];

  // Build path progressively
  let currentPath = '';
  for (const segment of segments) {
    currentPath += `/${segment}`;
    
    if (pathMap[currentPath]) {
      items.push(pathMap[currentPath]);
    } else if (segment === 'freelancer') {
      items.push({ label: 'Profile', href: null });
    } else if (segment === 'edit-listing') {
      items.push({ label: 'Edit Listing', href: null });
    } else if (segment === 'page') {
      items.push({ label: 'Page', href: null });
    }
  }

  return items;
}

export default Breadcrumb;
